// Main client-side application entry point
// This replaces the frontend/assets/js/app.js functionality

import { ClientAPIClient } from '../../utils/client-api';
import { ChartUtils } from '../../utils/chart-utils';
import { FormValidation } from '../../utils/form-validation';

// Import toast notification system
import '../css/toast-notifications.css';
declare global {
  interface Window {
    ToastNotificationManager: any;
    toast: any;
    getToastManager: any;
    toastIntegration: any;
  }
}

// Global application state
interface AppState {
  api: ClientAPIClient;
  charts: ChartUtils;
  forms: FormValidation;
  user: any;
  theme: 'light' | 'dark';
}

declare global {
  interface Window {
    App: AppState;
  }
}

class RecruitApp {
  private api: ClientAPIClient;
  private charts: ChartUtils;
  private forms: FormValidation;
  private state: any = {};

  constructor() {
    this.api = new ClientAPIClient();
    this.charts = new ChartUtils();
    this.forms = new FormValidation();
    
    this.init();
  }

  private init(): void {
    // Initialize application
    console.log('Recruit Platform - Client Application Initialized');
    
    // Initialize toast notification system first
    this.setupToastNotifications();
    
    // Setup global error handling
    this.setupErrorHandling();
    
    // Setup authentication monitoring
    this.setupAuthMonitoring();
    
    // Setup theme management
    this.setupThemeManagement();
    
    // Setup navigation
    this.setupNavigation();
    
    // Setup progressive web app features
    this.setupPWA();
    
    // Expose utilities globally for islands to use
    window.App = {
      api: this.api,
      charts: this.charts,
      forms: this.forms,
      user: this.state.user,
      theme: this.state.theme || 'light'
    };
    
    // Sync theme after window.App is initialized
    if (this.state.theme) {
      window.App.theme = this.state.theme;
    }
  }

  private async setupToastNotifications(): Promise<void> {
    try {
      // Load toast notification scripts
      await this.loadScript('/assets/js/toast-notifications.js');
      await this.loadScript('/assets/js/toast-integration.js');
      
      // Initialize with app-specific configuration
      const toastManager = window.getToastManager({
        position: 'top-right',
        duration: 5000,
        maxToasts: 4,
        animations: true,
        autoClose: true,
        showProgress: true,
        closeOnClick: false,
        pauseOnHover: true,
        newestFirst: true
      });
      
      console.log('Toast notification system initialized');
      
      // Show welcome toast on first visit
      if (!sessionStorage.getItem('app-initialized')) {
        toastManager.success(
          'Welcome to the Recruit Platform! You\'ll receive notifications for important updates.',
          {
            title: 'Welcome',
            duration: 6000
          }
        );
        sessionStorage.setItem('app-initialized', 'true');
      }
      
    } catch (error) {
      console.error('Failed to initialize toast notifications:', error);
    }
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  private setupErrorHandling(): void {
    window.addEventListener('error', (event) => {
      console.error('Global error:', event.error);
      
      // Use toast notifications if available
      if (window.toast) {
        window.toast.error(
          'An unexpected error occurred. Please refresh the page or contact support if the issue persists.',
          {
            title: 'Application Error',
            duration: 8000,
            actions: [{
              label: 'Refresh Page',
              style: 'primary',
              handler: () => window.location.reload()
            }]
          }
        );
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Use toast notifications if available
      if (window.toast) {
        window.toast.error(
          'A network or processing error occurred. Please try again.',
          {
            title: 'Unexpected Error',
            duration: 6000
          }
        );
      }
    });
  }

  private setupAuthMonitoring(): void {
    // Listen for auth failures
    window.addEventListener('auth-failed', (event) => {
      console.warn('Authentication failed:', event.detail);
      
      // Use toast notifications if available
      if (window.toast) {
        window.toast.warning(
          'Your session has expired. You will be redirected to the login page.',
          {
            title: 'Session Expired',
            duration: 4000,
            persistent: true,
            actions: [{
              label: 'Sign In Now',
              style: 'primary',
              handler: () => window.location.href = '/login'
            }]
          }
        );
      }
      
      // Redirect to login after a delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 4000);
    });

    // Check for stored token and user info
    const token = localStorage.getItem('access_token');
    if (token) {
      // Validate token and get user info
      this.validateToken();
    }
  }

  private async validateToken(): Promise<void> {
    try {
      const response = await this.api.get('/api/auth/me');
      if (response.success) {
        this.state.user = response.data;
        window.App.user = this.state.user;
      } else {
        // Token is invalid
        localStorage.removeItem('access_token');
      }
    } catch (error) {
      console.warn('Token validation failed:', error);
      localStorage.removeItem('access_token');
    }
  }

  private setupThemeManagement(): void {
    // Get saved theme or default to light
    const savedTheme = localStorage.getItem('theme') || 'light';
    this.state.theme = savedTheme;
    
    // Apply theme
    this.applyTheme(savedTheme as 'light' | 'dark');
    
    // Listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addListener(() => {
        if (!localStorage.getItem('theme')) {
          this.applyTheme(mediaQuery.matches ? 'dark' : 'light');
        }
      });
    }
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    this.state.theme = theme;
    
    // Only update window.App if it's initialized
    if (window.App) {
      window.App.theme = theme;
    }
    
    // Refresh chart colors if charts are initialized
    if (this.charts) {
      this.charts.refreshColors();
    }
  }

  private setupNavigation(): void {
    // Handle client-side navigation enhancements
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href^="/"]');
      
      if (link && !link.hasAttribute('data-no-spa')) {
        const href = link.getAttribute('href');
        if (href && href !== window.location.pathname) {
          // Add loading state to navigation
          this.showPageLoading();
        }
      }
    });
  }

  private setupPWA(): void {
    // Service worker registration
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }

    // App install prompt
    let deferredPrompt: any;
    window.addEventListener('beforeinstallprompt', (e) => {
      deferredPrompt = e;
      this.showInstallPrompt();
    });
  }

  private showPageLoading(): void {
    // Add page loading indicator
    const loader = document.createElement('div');
    loader.id = 'page-loader';
    loader.className = 'fixed top-0 left-0 w-full h-1 bg-blue-500 z-50 transition-all duration-300';
    loader.style.transform = 'translateX(-100%)';
    
    document.body.appendChild(loader);
    
    // Animate loading bar
    requestAnimationFrame(() => {
      loader.style.transform = 'translateX(0)';
    });
    
    // Remove loader after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        loader.remove();
      }, 300);
    }, { once: true });
  }

  private showInstallPrompt(): void {
    // Create install app prompt (can be customized)
    const banner = document.createElement('div');
    banner.className = 'fixed bottom-4 left-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-50 flex items-center justify-between';
    banner.innerHTML = `
      <div>
        <h3 class="font-semibold">Install Recruit Platform</h3>
        <p class="text-sm opacity-90">Install our app for a better experience</p>
      </div>
      <div class="flex gap-2">
        <button id="install-app" class="bg-white text-blue-600 px-3 py-1 rounded text-sm font-medium">Install</button>
        <button id="dismiss-install" class="opacity-70 hover:opacity-100 px-3 py-1 rounded text-sm">Dismiss</button>
      </div>
    `;
    
    document.body.appendChild(banner);
    
    // Handle install
    banner.querySelector('#install-app')?.addEventListener('click', async () => {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('Install outcome:', outcome);
        deferredPrompt = null;
      }
      banner.remove();
    });
    
    // Handle dismiss
    banner.querySelector('#dismiss-install')?.addEventListener('click', () => {
      banner.remove();
    });
  }

  public showToast(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration = 4000): void {
    const container = document.getElementById('toastContainer');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = `toast pointer-events-auto flex items-center p-4 mb-3 w-full max-w-sm rounded-lg shadow-lg transform translate-x-full transition-all duration-300 ease-out`;
    
    const typeStyles = {
      info: 'bg-blue-500 text-white',
      success: 'bg-green-500 text-white',
      warning: 'bg-yellow-500 text-black',
      error: 'bg-red-500 text-white'
    };
    
    const icons = {
      info: '🛈',
      success: '✓',
      warning: '⚠',
      error: '✕'
    };
    
    toast.classList.add(...typeStyles[type].split(' '));
    toast.innerHTML = `
      <div class="flex-shrink-0 text-lg mr-3">${icons[type]}</div>
      <div class="text-sm font-medium flex-1">${message}</div>
      <button class="ml-4 flex-shrink-0 p-1.5 rounded-lg hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    `;
    
    container.appendChild(toast);
    
    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full');
      toast.classList.add('translate-x-0');
    });
    
    // Auto-hide
    const hideToast = () => {
      toast.classList.add('translate-x-full', 'opacity-0');
      setTimeout(() => {
        if (container.contains(toast)) {
          container.removeChild(toast);
        }
      }, 300);
    };
    
    setTimeout(hideToast, duration);
    
    // Manual close
    toast.querySelector('button')?.addEventListener('click', hideToast);
  }

  // Public API for islands and other components
  public getAPI(): ClientAPIClient {
    return this.api;
  }

  public getCharts(): ChartUtils {
    return this.charts;
  }

  public getForms(): FormValidation {
    return this.forms;
  }

  public toggleTheme(): void {
    const newTheme = this.state.theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);
    this.applyTheme(newTheme);
  }
}

// Initialize application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new RecruitApp());
} else {
  new RecruitApp();
}

export { RecruitApp };